import { memo, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  ChevronUp,
  Clock,
  Compass,
  Dna,
  Download,
  GitCommitVertical,
  Layers,
  Maximize,
  Minus,
  Network,
  Palette,
  PictureInPicture2,
  Plus,
  Printer,
  SlidersHorizontal,
} from 'lucide-react';
import { useTreeFeatures } from '@/hooks/useTreeFeatures';

// ─── Generationen-Optionen ──────────────────────────────────────────────────

const DEPTH_OPTIONS: Array<{ value: number | null; label: string }> = [
  { value: null, label: 'Alle' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
  { value: 5, label: '5' },
  { value: 6, label: '6' },
  { value: 8, label: '8' },
  { value: 10, label: '10' },
];

// ─── Bausteine ──────────────────────────────────────────────────────────────
//
// Die Leiste war eine Reihe beschrifteter Einzelknöpfe, die bei schmalem
// Fenster in zwei, drei Zeilen umbrach und den Baum von unten zuwuchs. Jetzt
// ist sie EINE Zeile: Symbolknöpfe für das, was man ständig umschaltet, und
// zwei Menüs für Einstellungen (Ansicht) und Ausgabe (Export). Die Namen
// stehen im Tooltip und – wo Platz ist – daneben.

const BAR_BTN =
  'relative flex h-8 items-center justify-center gap-1.5 rounded-lg px-2 text-xs font-medium transition-colors ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 disabled:cursor-not-allowed disabled:opacity-40';

function barBtnClass(active = false): string {
  return `${BAR_BTN} ${
    active
      ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
      : 'text-slate-600 hover:bg-slate-100 active:bg-slate-200'
  }`;
}

function Trenner() {
  return <span className="mx-0.5 h-5 w-px shrink-0 bg-slate-200" aria-hidden="true" />;
}

function Punkt({ farbe = 'bg-blue-500' }: { farbe?: string }) {
  return (
    <span
      className={`absolute right-0.5 top-0.5 h-2 w-2 rounded-full border border-white ${farbe}`}
      aria-hidden="true"
    />
  );
}

/** Ein Menü, das sich nach oben öffnet – die Leiste liegt am unteren Rand. */
function Aufklapper({
  label,
  icon,
  active,
  punkt,
  ausrichtung = 'left',
  labelKlasse = 'hidden lg:inline',
  children,
}: {
  label: string;
  icon: ReactNode;
  active?: boolean;
  punkt?: ReactNode;
  ausrichtung?: 'left' | 'right';
  labelKlasse?: string;
  children: (schliessen: () => void) => ReactNode;
}) {
  const [offen, setOffen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!offen) return;
    function klick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOffen(false);
    }
    function taste(e: KeyboardEvent) {
      if (e.key === 'Escape') setOffen(false);
    }
    document.addEventListener('mousedown', klick);
    document.addEventListener('keydown', taste);
    return () => {
      document.removeEventListener('mousedown', klick);
      document.removeEventListener('keydown', taste);
    };
  }, [offen]);

  return (
    // Mobil hängt das Menü an der ganzen Leiste (sie ist positioniert), sonst
    // ragte es rechts aus dem Bild; ab sm am eigenen Knopf.
    <div className="sm:relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOffen((v) => !v)}
        aria-expanded={offen}
        aria-haspopup="menu"
        aria-label={label}
        title={label}
        className={barBtnClass(offen || active)}
      >
        {icon}
        <span className={labelKlasse}>{label}</span>
        <ChevronUp
          className={`hidden h-3 w-3 transition-transform lg:block ${offen ? '' : 'rotate-180'}`}
          aria-hidden="true"
        />
        {punkt}
      </button>
      {offen && (
        <div
          role="menu"
          className={`absolute bottom-full z-30 mb-2 w-64 max-w-[calc(100vw-2rem)] rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl ${
            ausrichtung === 'right' ? 'left-0 sm:left-auto sm:right-0' : 'left-0'
          }`}
        >
          {children(() => setOffen(false))}
        </div>
      )}
    </div>
  );
}

/** Eine Zeile im Menü, die etwas ein- oder ausschaltet. */
function MenueSchalter({
  icon,
  label,
  hinweis,
  an,
  disabled,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  hinweis?: string;
  an: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitemcheckbox"
      aria-checked={an}
      disabled={disabled}
      onClick={onClick}
      className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
    >
      <span className={an ? 'text-blue-600' : 'text-slate-400'}>{icon}</span>
      <span className="flex-1">
        <span className="block leading-tight">{label}</span>
        {hinweis && <span className="block text-[11px] leading-tight text-slate-400">{hinweis}</span>}
      </span>
      <span
        className={`flex h-4 w-7 shrink-0 items-center rounded-full p-0.5 transition-colors ${
          an ? 'bg-blue-600' : 'bg-slate-200'
        }`}
        aria-hidden="true"
      >
        <span className={`h-3 w-3 rounded-full bg-white shadow transition-transform ${an ? 'translate-x-3' : ''}`} />
      </span>
    </button>
  );
}

function MenueAktion({
  icon,
  label,
  hinweis,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  hinweis?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
    >
      <span className="text-slate-400">{icon}</span>
      <span className="flex-1">
        <span className="block leading-tight">{label}</span>
        {hinweis && <span className="block text-[11px] leading-tight text-slate-400">{hinweis}</span>}
      </span>
    </button>
  );
}

function MenueTitel({ children }: { children: ReactNode }) {
  return (
    <p className="px-2.5 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
      {children}
    </p>
  );
}

// ─── TreeToolbar ────────────────────────────────────────────────────────────

interface TreeToolbarProps {
  zoomLevel: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onCenter: () => void;
  nodeCount: number;
  linkCount: number;
  generationDepth: number | null;
  onGenerationDepthChange: (depth: number | null) => void;
  hasSelectedPerson: boolean;
  /** „Eigene Linie zentrieren" aktiv? */
  lineageActive: boolean;
  /** Kann der Linien-Fokus umgeschaltet werden? (Person ausgewählt oder bereits aktiv) */
  canToggleLineage: boolean;
  onToggleLineage: () => void;
  coloredLines: boolean;
  onColoredLinesChange: (on: boolean) => void;
  /** Ganzen Baum als SVG-Datei exportieren. */
  onExportSvg: () => void;
  /** Ganzen Baum als mehrseitiges PDF drucken. */
  onPrintPdf: () => void;
  /** Zeit-Schieberegler aktiv? */
  timeActive: boolean;
  /** Gibt es überhaupt Datumsdaten für die Zeitreise? */
  canTimeTravel: boolean;
  onToggleTimeTravel: () => void;
  /** Übersichtskarte (Minimap) sichtbar? */
  minimapOpen: boolean;
  onToggleMinimap: () => void;
  /** Erkundungsansicht aktiv? */
  erkundung: boolean;
  onToggleErkundung: () => void;
  /** Dialog „Vorfahren aufdecken" öffnen. */
  onVorfahren: () => void;
  /** Ist der Baum groß genug, dass die Erkundungsansicht sich lohnt? */
  vieleP: boolean;
  /** Wie viele Personen gerade GEZEICHNET werden (Erkundung). Sonst gleich nodeCount. */
  sichtbareP: number;
  /** Verfügbare Medizin-Overlays (leer/undefined → Selector ausgeblendet). */
  overlayOptions?: Array<{ catalogId: string; label: string }>;
  activeOverlayId?: string | null;
  onOverlayChange?: (opt: { catalogId: string; label: string } | null) => void;
}

export const TreeToolbar = memo(function TreeToolbar({
  zoomLevel,
  onZoomIn,
  onZoomOut,
  onCenter,
  nodeCount,
  linkCount,
  generationDepth,
  onGenerationDepthChange,
  hasSelectedPerson,
  lineageActive,
  canToggleLineage,
  onToggleLineage,
  coloredLines,
  onColoredLinesChange,
  onExportSvg,
  onPrintPdf,
  timeActive,
  canTimeTravel,
  onToggleTimeTravel,
  minimapOpen,
  onToggleMinimap,
  erkundung,
  onToggleErkundung,
  onVorfahren,
  vieleP,
  sichtbareP,
  overlayOptions,
  activeOverlayId,
  onOverlayChange,
}: TreeToolbarProps) {
  const { hat } = useTreeFeatures();
  // SVG und PDF entstehen vollständig im Browser – der Server sieht davon
  // nichts und kann sie deshalb auch nicht abweisen. Für diese beiden
  // Funktionen ist das Ausblenden hier die EINZIGE Durchsetzung des Tarifs,
  // nicht bloß eine Bequemlichkeit.
  const darfSvg = hat('export.svg');
  const darfPdf = hat('export.pdf');

  const zoomPercent = Math.round(zoomLevel * 100);
  const hasOverlaySelector = !!(overlayOptions && overlayOptions.length > 0 && onOverlayChange);
  const wartetAufPerson = generationDepth !== null && !hasSelectedPerson;
  // Punkt am „Ansicht"-Knopf, sobald dort etwas den Baum EINSCHRÄNKT oder
  // umfärbt – sonst sucht man, warum er anders aussieht. Farbige Linien sind
  // die Vorgabe und zählen deshalb nicht.
  const ansichtVeraendert = generationDepth !== null || !!activeOverlayId;

  // In der Erkundung zählt, was auf der Fläche steht – und wovon. „812
  // Personen" über einem Bild mit sieben Kästchen wäre schlicht falsch.
  const zaehler = erkundung
    ? `${sichtbareP} von ${nodeCount} Personen`
    : `${nodeCount} Personen · ${linkCount} Beziehungen`;

  const lineageTitle = lineageActive
    ? 'Linien-Zentrierung aufheben'
    : canToggleLineage
      ? 'Direkte Vorfahren-Linie der ausgewählten Person senkrecht ausrichten'
      : 'Person auswählen, um ihre Linie zu zentrieren';
  const timeTitle = !canTimeTravel
    ? 'Keine Datumsangaben vorhanden'
    : timeActive
      ? 'Zeitreise beenden'
      : 'Zeitreise: Baum zu einem bestimmten Jahr anzeigen';
  // „Erkunden" hieße dasselbe wie das Menü in der Navigationsleiste – zwei
  // Dinge mit einem Namen. „Aufdecken" sagt, was passiert.
  const erkundungTitle = erkundung
    ? 'Wieder den ganzen Stammbaum zeigen'
    : 'Bei einer Person beginnen und die Verwandtschaft nach und nach aufdecken';
  const vorfahrenTitle = 'Person suchen und nur ihre Vorfahren aufdecken (Eltern, deren Eltern, …)';

  // ─── Schnellknöpfe (ab sm in der Leiste, darunter im Ansicht-Menü) ────────

  const schnell = (
    <>
      <button
        type="button"
        onClick={onToggleLineage}
        disabled={!canToggleLineage}
        className={barBtnClass(lineageActive)}
        title={lineageTitle}
        aria-label="Linie zentrieren"
        aria-pressed={lineageActive}
      >
        <GitCommitVertical className="h-4 w-4" aria-hidden="true" />
        <span className="hidden xl:inline">Linie</span>
      </button>
      <button
        type="button"
        onClick={onToggleTimeTravel}
        disabled={!canTimeTravel}
        className={barBtnClass(timeActive)}
        title={timeTitle}
        aria-label="Zeitreise"
        aria-pressed={timeActive}
      >
        <Clock className="h-4 w-4" aria-hidden="true" />
        <span className="hidden xl:inline">Zeit</span>
      </button>
      {/* Der Punkt erscheint erst ab vielen Personen – als Hinweis, nicht als
          Aufforderung. Eingeschaltet wird die Erkundung NIEMALS von selbst. */}
      <button
        type="button"
        onClick={onToggleErkundung}
        className={barBtnClass(erkundung)}
        title={erkundungTitle}
        aria-label="Aufdecken"
        aria-pressed={erkundung}
      >
        <Compass className="h-4 w-4" aria-hidden="true" />
        <span className="hidden xl:inline">Aufdecken</span>
        {!erkundung && vieleP && <Punkt />}
      </button>
      <button
        type="button"
        onClick={onVorfahren}
        className={barBtnClass(false)}
        title={vorfahrenTitle}
        aria-label="Vorfahren aufdecken"
      >
        <Network className="h-4 w-4" aria-hidden="true" />
        <span className="hidden xl:inline">Vorfahren</span>
      </button>
    </>
  );

  return (
    <div
      className="absolute left-4 z-10 flex max-w-[calc(100%-2rem)] items-center gap-0.5 rounded-xl border border-slate-200 bg-white/95 p-1 shadow-md backdrop-blur"
      // Über dem iOS-Home-Indicator halten (sonst verdeckt er die Toolbar).
      style={{ bottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
      role="toolbar"
      aria-label="Ansicht des Stammbaums"
    >
      {/* Zoom */}
      <button type="button" onClick={onZoomOut} className={barBtnClass()} aria-label="Herauszoomen" title="Herauszoomen">
        <Minus className="h-4 w-4" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={onCenter}
        className={`${barBtnClass()} min-w-[3.25rem] tabular-nums`}
        aria-label={`Zoom ${zoomPercent} % – Ansicht einpassen`}
        title="Ansicht einpassen"
      >
        {zoomPercent}%
      </button>
      <button type="button" onClick={onZoomIn} className={barBtnClass()} aria-label="Hineinzoomen" title="Hineinzoomen">
        <Plus className="h-4 w-4" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={onCenter}
        className={`${barBtnClass()} hidden sm:flex`}
        aria-label="Ansicht zentrieren"
        title="Ansicht zentrieren"
      >
        <Maximize className="h-4 w-4" aria-hidden="true" />
      </button>

      <Trenner />

      {/* Schnellknöpfe – ab Tablet-Breite direkt in der Leiste */}
      <div className="hidden items-center gap-0.5 sm:flex">
        {schnell}
        <Trenner />
      </div>

      {/* Ansicht: alles, was man einstellt und dann stehen lässt */}
      <Aufklapper
        label="Ansicht"
        icon={<SlidersHorizontal className="h-4 w-4" aria-hidden="true" />}
        active={ansichtVeraendert}
        punkt={wartetAufPerson ? <Punkt farbe="bg-amber-500" /> : ansichtVeraendert ? <Punkt /> : null}
      >
        {() => (
          <div className="space-y-0.5">
            {/* Mobil gibt es keine Schnellknöpfe in der Leiste – dann hier. */}
            <div className="sm:hidden">
              <MenueTitel>Werkzeuge</MenueTitel>
              <MenueSchalter
                icon={<GitCommitVertical className="h-4 w-4" />}
                label="Linie zentrieren"
                hinweis={canToggleLineage ? undefined : 'Erst eine Person auswählen'}
                an={lineageActive}
                disabled={!canToggleLineage}
                onClick={onToggleLineage}
              />
              <MenueSchalter
                icon={<Clock className="h-4 w-4" />}
                label="Zeitreise"
                hinweis={canTimeTravel ? undefined : 'Keine Datumsangaben vorhanden'}
                an={timeActive}
                disabled={!canTimeTravel}
                onClick={onToggleTimeTravel}
              />
              <MenueSchalter
                icon={<Compass className="h-4 w-4" />}
                label="Aufdecken"
                hinweis="Verwandtschaft Schritt für Schritt zeigen"
                an={erkundung}
                onClick={onToggleErkundung}
              />
              <MenueAktion icon={<Network className="h-4 w-4" />} label="Vorfahren aufdecken …" onClick={onVorfahren} />
              <div className="my-1 border-t border-slate-100" />
            </div>

            <MenueTitel>Darstellung</MenueTitel>
            <label className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-slate-700">
              <Layers className={`h-4 w-4 ${generationDepth !== null ? 'text-blue-600' : 'text-slate-400'}`} aria-hidden="true" />
              <span className="flex-1 leading-tight">
                Generationen
                {wartetAufPerson && (
                  <span className="block text-[11px] text-amber-600">Person im Baum auswählen</span>
                )}
              </span>
              <select
                value={generationDepth ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  onGenerationDepthChange(val === '' ? null : parseInt(val, 10));
                }}
                className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                aria-label="Anzahl sichtbarer Generationen"
              >
                {DEPTH_OPTIONS.map((opt) => (
                  <option key={opt.label} value={opt.value ?? ''}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <MenueSchalter
              icon={<Palette className="h-4 w-4" />}
              label="Farbige Linien"
              an={coloredLines}
              onClick={() => onColoredLinesChange(!coloredLines)}
            />
            {/* Die Übersichtskarte wird am Handy nicht angezeigt. */}
            <div className="hidden sm:block">
              <MenueSchalter
                icon={<PictureInPicture2 className="h-4 w-4" />}
                label="Übersichtskarte"
                an={minimapOpen}
                onClick={onToggleMinimap}
              />
            </div>
            {hasOverlaySelector && (
              <label className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-slate-700">
                <Dna className={`h-4 w-4 ${activeOverlayId ? 'text-violet-600' : 'text-slate-400'}`} aria-hidden="true" />
                <span className="flex-1 leading-tight">Medizin</span>
                <select
                  value={activeOverlayId ?? ''}
                  onChange={(e) => {
                    const opt = overlayOptions!.find((o) => o.catalogId === e.target.value);
                    onOverlayChange!(opt ?? null);
                  }}
                  className="max-w-[8.5rem] rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  aria-label="Medizin-Overlay auswählen"
                >
                  <option value="">Kein Overlay</option>
                  {overlayOptions!.map((o) => (
                    <option key={o.catalogId} value={o.catalogId}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <p className="mt-1 border-t border-slate-100 px-2.5 pb-1 pt-2 text-xs text-slate-400">{zaehler}</p>
          </div>
        )}
      </Aufklapper>

      {/* Export – nur, wenn der Tarif mindestens eines der Formate enthält */}
      {(darfSvg || darfPdf) && (
        <Aufklapper
          label="Export"
          icon={<Download className="h-4 w-4" aria-hidden="true" />}
          ausrichtung="right"
          labelKlasse="hidden xl:inline"
        >
          {(schliessen) => (
            <div>
              <MenueTitel>Ganzen Stammbaum ausgeben</MenueTitel>
              {darfSvg && (
                <MenueAktion
                  icon={<Download className="h-4 w-4" />}
                  label="Als SVG-Datei"
                  hinweis="Verlustfrei skalierbar, zum Weiterbearbeiten"
                  onClick={() => {
                    schliessen();
                    onExportSvg();
                  }}
                />
              )}
              {darfPdf && (
                <MenueAktion
                  icon={<Printer className="h-4 w-4" />}
                  label="Als PDF drucken"
                  hinweis="Auf A4-Seiten gekachelt"
                  onClick={() => {
                    schliessen();
                    onPrintPdf();
                  }}
                />
              )}
            </div>
          )}
        </Aufklapper>
      )}

      {/* Zähler nur, wenn wirklich Platz ist – sonst steht er im Ansicht-Menü. */}
      <span className="hidden whitespace-nowrap px-2 text-xs tabular-nums text-slate-400 2xl:inline">
        {zaehler}
      </span>
    </div>
  );
});
